import { ChangeDetectionStrategy, Component, Inject } from '@angular/core';
import { DynamicDialogRef } from "primeng/dynamicdialog";
import { FormControl, FormGroup, ReactiveFormsModule, Validators } from "@angular/forms";
import {
  ESeverity,
  IInputTextOptions,
  InputTextComponent,
  IToastService,
  toastServiceInjectionToken
} from "@zaeper/angular-dashboard-view-lib";
import { Router } from "@angular/router";
import { Button } from "primeng/button";
import { Checkbox } from "primeng/checkbox";
import { ZDictionaryPipe } from '@zaeper/angular-localization-lib';
import { launcherRestClientInjectionToken } from "../../rest-clients/application/launcher/launcher-rest-client-injection-token";
import { ILauncherRestClient } from "../../rest-clients/application/launcher/i-launcher-rest.client";
import { ICreateRecordResponse } from '@zaeper/communication-lib';

const IDENTIFIER_PATTERN: RegExp = /^[a-z0-9]+(-[a-z0-9]+)*$/;

@Component( {
  templateUrl: './create-launcher-modal.component.html',
  imports: [
    Button, ZDictionaryPipe, ReactiveFormsModule, InputTextComponent, Checkbox
  ],
  changeDetection: ChangeDetectionStrategy.OnPush
} )

export class CreateLauncherModalComponent {
  public readonly nameInputTextOptions: IInputTextOptions;
  public readonly identifierInputTextOptions: IInputTextOptions;
  public readonly formGroup: FormGroup;

  constructor(
    @Inject( launcherRestClientInjectionToken ) private readonly _launcherRestClient: ILauncherRestClient,
    @Inject( toastServiceInjectionToken ) private readonly _toastService: IToastService,
    private readonly _dynamicDialogRef: DynamicDialogRef,
    private readonly _router: Router
  ) {
    this.formGroup = new FormGroup( {
      name: new FormControl( '', {
        validators: [ Validators.required, Validators.minLength( 3 ) ],
      } ),
      identifier: new FormControl( '', {
        validators: [
          Validators.minLength( 2 ),
          Validators.maxLength( 63 ),
          Validators.pattern( IDENTIFIER_PATTERN )
        ],
      } ),
      systemApp: new FormControl( false )
    }, {
      updateOn: "submit"
    } );

    this.nameInputTextOptions = {
      title: 'Name',
      inputId: 'launcher-name',
      placeholder: 'Name',
      required: true,
      formControlEntry: {
        formControl: this.formGroup.controls["name"] as FormControl<string>,
        key: 'name'
      }
    }

    this.identifierInputTextOptions = {
      title: 'Identifier',
      inputId: 'launcher-identifier',
      placeholder: 'e.g. my-launcher (optional)',
      required: false,
      formControlEntry: {
        formControl: this.formGroup.controls["identifier"] as FormControl<string>,
        key: 'identifier'
      }
    }
  }

  public close(): void {
    this._dynamicDialogRef.close();
  }

  public submit(event: SubmitEvent): void {
    if ( this.formGroup.valid ) {
      const name: string = this.formGroup.get( "name" )?.value;
      const identifierRaw: string = this.formGroup.get( "identifier" )?.value;
      const identifier: string | undefined = identifierRaw && identifierRaw.length > 0 ? identifierRaw : undefined;
      const systemApp: boolean = !!this.formGroup.get( "systemApp" )?.value;

      this._launcherRestClient.createApplication( {
        name,
        identifier,
        systemApp
      } ).then( (createRecordResponse: ICreateRecordResponse) => {
        this._router.navigate( [
          "de", "launcher", createRecordResponse.recordId
        ] ).then( () => {
          this._toastService.setMessage( {
            severity: ESeverity.success,
            title: "Erfolgreich",
            detail: "Launcher wurde erfolgreich erstellt"
          } )
          this.close();
        } )
      } ).catch( () => {
        this._toastService.setMessage( {
          severity: ESeverity.error,
          title: "Fehlgeschlagen",
          detail: "Launcher konnte nicht erstellt werden"
        } )
      } )
    }
  }
}
